import { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { AdditiveBlending, BufferAttribute, BufferGeometry, ShaderMaterial } from 'three';
import { seededRandom } from './random.js';
import { useCelestial } from './CelestialProvider.jsx';

const DUST_TEXT = 'Proof, not a promise.';
const DUST_FONT = '700 74px "Space Grotesk Dust", system-ui, sans-serif';

// Magic-dust morph (faithful to the supplied component's behavior, sanitized):
// particles fly from a scatter cloud into the sampled glyph positions, each on
// its own delay — driven by uProgress (the beat's dwell t), not wall clock,
// so scrubbing the scroll scrubs the assembly.
const VERTEX = /* glsl */ `
  attribute vec3 aScatter;
  attribute float aDelay;
  uniform float uProgress;
  uniform float uSize;
  varying float vAlpha;
  void main() {
    float k = smoothstep(aDelay, aDelay + 0.38, uProgress);
    vec3 pos = mix(aScatter, position, k);
    // gentle swirl while in flight
    float flight = k * (1.0 - k) * 4.0;
    pos.x += sin(aDelay * 40.0 + k * 9.0) * 0.08 * flight;
    pos.y += cos(aDelay * 31.0 + k * 7.0) * 0.06 * flight;
    vec4 mv = modelViewMatrix * vec4(pos, 1.0);
    gl_PointSize = clamp(uSize * (1.0 + flight * 0.8) * (52.0 / -mv.z), 1.0, 7.0);
    gl_Position = projectionMatrix * mv;
    vAlpha = 0.14 + 0.86 * k;
  }
`;

const FRAGMENT = /* glsl */ `
  varying float vAlpha;
  void main() {
    vec2 c = gl_PointCoord - 0.5;
    float d = smoothstep(0.5, 0.08, length(c));
    gl_FragColor = vec4(1.0, 0.86, 0.62, d * vAlpha);
  }
`;

export function DustToText({ progressRef, position = [0, 0, 0], rotation = [0, 0, 0], scale = 1 }) {
  const { perfTier, seeds } = useCelestial();
  const [geometry, setGeometry] = useState(null);
  const materialRef = useRef();

  useEffect(() => {
    let cancelled = false;
    async function build() {
      if (document.fonts?.load) await document.fonts.load(DUST_FONT, DUST_TEXT);
      if (document.fonts?.ready) await document.fonts.ready;
      if (!cancelled) setGeometry(buildDustGeometry(perfTier.tier, seeds.dustText));
    }
    build();
    return () => {
      cancelled = true;
    };
  }, [perfTier.tier, seeds.dustText]);

  useEffect(() => () => geometry?.dispose(), [geometry]);

  const material = useMemo(
    () =>
      new ShaderMaterial({
        vertexShader: VERTEX,
        fragmentShader: FRAGMENT,
        uniforms: { uProgress: { value: 0 }, uSize: { value: 1.15 } },
        transparent: true,
        depthWrite: false,
        blending: AdditiveBlending,
      }),
    [],
  );

  useFrame(() => {
    const target = progressRef?.current ?? 1;
    material.uniforms.uProgress.value = target;
  });

  if (!geometry) return null;

  return <points geometry={geometry} material={material} position={position} rotation={rotation} scale={scale} ref={materialRef} />;
}

function buildDustGeometry(tier, seed) {
  const canvas = document.createElement('canvas');
  canvas.width = 900;
  canvas.height = 180;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.font = DUST_FONT;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(DUST_TEXT, canvas.width / 2, canvas.height / 2);
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  const max = tier >= 2 ? 2600 : 1300;
  const random = seededRandom(seed);
  const targets = [];
  for (let y = 0; y < canvas.height; y += 2) {
    for (let x = 0; x < canvas.width; x += 2) {
      if (data[(y * canvas.width + x) * 4 + 3] < 80 || random() < 0.55) continue;
      targets.push([
        (x / canvas.width - 0.5) * 5.2 + (random() - 0.5) * 0.015,
        (0.5 - y / canvas.height) * 1.05,
        (random() - 0.5) * 0.05,
      ]);
      if (targets.length >= max) break;
    }
    if (targets.length >= max) break;
  }
  const count = targets.length;
  const positions = new Float32Array(count * 3);
  const scatter = new Float32Array(count * 3);
  const delays = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    positions[i * 3] = targets[i][0];
    positions[i * 3 + 1] = targets[i][1];
    positions[i * 3 + 2] = targets[i][2];
    scatter[i * 3] = (random() - 0.5) * 7;
    scatter[i * 3 + 1] = (random() - 0.5) * 3.4 - 0.6;
    scatter[i * 3 + 2] = (random() - 0.5) * 3.2;
    delays[i] = random() * 0.42;
  }
  const geo = new BufferGeometry();
  geo.setAttribute('position', new BufferAttribute(positions, 3));
  geo.setAttribute('aScatter', new BufferAttribute(scatter, 3));
  geo.setAttribute('aDelay', new BufferAttribute(delays, 1));
  return geo;
}
